import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { ICustomColumn, ImportedDeals } from '../../../Types/types';
import { getColumns, getTransformedImportedDeals } from '../../ImportedDeals/importedDealsUtils';
import ImportedDealsTable from '../../ImportedDealTable/ImportedDealsTable';
import { Card } from '@sede-enterprise/shell-ds-react-framework';
import { setOutPlanDealsId, setImportedDealsData, setIsReset, setSelectedAddToChartDeals } from '../../../redux/dashboard/dashboardSlice';
import { ALL_COUNTRIES, ALL_REGIONS } from '../../../api/constants';
import { getImportedDeals } from '../../../api/services';
import { currentYear } from '../../../utils/utils';
import { one } from '../../../constants';
import { notify } from '../../Toast/notify';
import { FETCH_ERROR } from '../../../utils/constants';
import { globalApiErrorHandler } from '../../../../Tanso/components/Utils/utils';

const DealFunnel = (props: { selectedRegion: string; selectedSubRegion: string }) => {
  const { selectedSubRegion, selectedRegion } = props;
  const dispatch = useAppDispatch();
  const { importedDeals, isReset, loggedInUserRegion, userEvp } = useAppSelector((state) => state.dashboard);
  const [updatedImportedDeals, setUpdatedImportedDeals] = useState<ImportedDeals[]>([]);
  const [importedDealApiCalled, setImportedDealApiCalled] = useState(false);
  const transformImportedDeals = useCallback(
    (deals: ImportedDeals[]) => {
      const filterFutureYearDeals = deals?.filter((deal) => {
        const startYear = new Date(deal.START_DATE).getFullYear();
        return startYear >= currentYear;
      });
      const selectedSubRegionDeals =
        selectedRegion === ALL_REGIONS && selectedSubRegion === ALL_COUNTRIES
          ? filterFutureYearDeals
          : filterFutureYearDeals.filter((deal) => {
            if (selectedSubRegion === ALL_COUNTRIES) {
              return deal.REGION_NAME === selectedRegion;
            } else {
              return deal.COUNTRY_NAME === selectedSubRegion;
            }
          });
      return getTransformedImportedDeals(selectedSubRegionDeals) as ImportedDeals[];
    },
    [selectedRegion, selectedSubRegion],
  );
  const getImportedDealsData = useCallback(async () => {
    try {
      const importedDealsData = await getImportedDeals(loggedInUserRegion, userEvp);
      setUpdatedImportedDeals(transformImportedDeals(importedDealsData.data));
      dispatch(setImportedDealsData(importedDealsData.data));
    } catch (error) {
      globalApiErrorHandler('Deal Funnel', error)
      notify('error', FETCH_ERROR);
    }
  }, [dispatch, loggedInUserRegion, transformImportedDeals, userEvp]);

  useEffect(() => {
    if (!importedDeals.length && !importedDealApiCalled) {
      getImportedDealsData();
      setImportedDealApiCalled(true);
    }
  }, [getImportedDealsData, importedDeals, importedDealApiCalled]);

  const getUpdatedImportedDeals = () => {
    setUpdatedImportedDeals(transformImportedDeals(importedDeals));
    dispatch(setOutPlanDealsId([]));
  }

  useEffect(() => {
    getUpdatedImportedDeals();
  }, [importedDeals, transformImportedDeals, getImportedDealsData, dispatch]);

  useEffect(() => {
    // It will only call the getUpdatedImportedDeals when it is reset using Reset button.
    isReset && getUpdatedImportedDeals();
    dispatch(setIsReset(false));
  }, [isReset, dispatch]);

  const getAddedImportedDealsId = (updatedData: ImportedDeals[]) => {
    const addedImportedDealsIdIds = updatedData
      .filter((item: ImportedDeals) => item.ADD_TO_CHART === one && item.IS_OPERATING_PLAN !== one)
      .map((item: ImportedDeals) => item.OPPORTUNITY_ID);
    dispatch(setOutPlanDealsId(addedImportedDealsIdIds));
  }

  const handleToggle = (selectedItem: ImportedDeals) => {
    const updatedData = updatedImportedDeals.map((item: ImportedDeals) =>
      item.OPPORTUNITY_ID === selectedItem.OPPORTUNITY_ID
        ? { ...item, ADD_TO_CHART: selectedItem.ADD_TO_CHART === 0 ? one : 0 }
        : item,
    );
    dispatch(setSelectedAddToChartDeals(selectedItem));
    getAddedImportedDealsId(updatedData);
    setUpdatedImportedDeals(updatedData);
  };

  return (
    <Card
      data-testid='deal-funnel'
      header={{
        title: 'Imported Deals in carbon dashboard',
        description: `No. of Imported Deals: ${updatedImportedDeals.length}`,
      }}
    >
      <ImportedDealsTable
        importedDeals={updatedImportedDeals}
        cols={getColumns(null, handleToggle) as ICustomColumn[]}
      />
    </Card>
  );
};

export default DealFunnel;
